import Meter from '../../assets/meter.png'
import Alert from '../../assets/alert.png'
import Issues from '../../assets/issues.png'
import Calendar from '../../assets/calendar.png'
import API from '../../services/api.js';
import { useState, useEffect, useCallback } from "react"
import { useNavigate } from 'react-router-dom'
import { Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { CiPlay1 } from "react-icons/ci";
import LoadingPopup from '../misc/Loading.js'


import Reports from './Reports.js'

const Dashboard = () => {


  const navigate = useNavigate()

  const [fetchedclientdashboard, setFetchedClientDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(null);
  const [governanceScore, setGovernanceScore] = useState(null);
  const selectedApp = sessionStorage.getItem("selectedApp")

  const fetchClientDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await API.post(`${process.env.REACT_APP_DJANGO_API_URL}/client-Dashboard/`, {"selectedApp":selectedApp}
      );
      setFetchedClientDashboard(response.data);
      sessionStorage.setItem('asset_id', response.data.asset_id);
    } catch (err) {
      setError(err.message || "Failed to fetch dashboard data");
      console.error("Error fetching JSON:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedApp]);

  const fetchGovernanceScore = useCallback(async () => {
    try {
      const response = await API.get('user-score/', {
          params: { assetName: selectedApp }
        });
      setGovernanceScore(response.data);
    } catch (err) {
      console.error("Error fetching governance score:", err);
    }
  }, [selectedApp]);

  useEffect(() => {
    const controller = new AbortController();
    
    fetchClientDashboard();
    fetchGovernanceScore();

    return () => controller.abort();
  }, [fetchClientDashboard, fetchGovernanceScore]);

  const scanCount = fetchedclientdashboard?.scan_count || 0;
  const issueCount = fetchedclientdashboard?.issues_count || 0;
  const lastScan = fetchedclientdashboard?.latest_addition || 0;
  const date = fetchedclientdashboard?.time;
  const top5IDs = fetchedclientdashboard?.top_5_ids || [];
  const top5IDFailCount = fetchedclientdashboard?.top_5_fail_counts || [];
  const top5IDPassCount = fetchedclientdashboard?.top_5_pass_counts || [];
  const months = fetchedclientdashboard?.months || [];
  const monthScore = fetchedclientdashboard?.monthScore || [];
  const lastScanTime = fetchedclientdashboard?.last_scan_time || "N/A";
  const score = fetchedclientdashboard?.score || 0;
  const applicationData = fetchedclientdashboard?.applicationData || {};

  sessionStorage.setItem('applicationData', JSON.stringify(applicationData));

  const handleNewScan = () => {
    navigate('/newscan');
  };

  const percentage = (score / 100) * 100;


  const data = [
    { month: months[0], score: monthScore[0] },
    { month: months[1], score: monthScore[1] },
    { month: months[2], score: monthScore[2] },
    { month: months[3], score: monthScore[3] },
    { month: months[4], score: monthScore[4] },
    { month: months[5], score: monthScore[5] }
  ];
  const top5IDTotalCount = top5IDFailCount.map((failCount, index) => failCount + top5IDPassCount[index]);

  const detectors = [
    { label: top5IDs[0], failCount: top5IDFailCount[0], passCount: top5IDPassCount[0], score: ((top5IDFailCount[0]/top5IDTotalCount[0])*100).toFixed(1) , total: top5IDTotalCount[0] },
    { label: top5IDs[1], failCount: top5IDFailCount[1], passCount: top5IDPassCount[1], score: ((top5IDFailCount[1]/top5IDTotalCount[1])*100).toFixed(1) , total: top5IDTotalCount[1] },  
    { label: top5IDs[2], failCount: top5IDFailCount[2], passCount: top5IDPassCount[2], score: ((top5IDFailCount[2]/top5IDTotalCount[2])*100).toFixed(1) , total: top5IDTotalCount[2] },
    { label: top5IDs[3], failCount: top5IDFailCount[3], passCount: top5IDPassCount[3], score: (top5IDFailCount[3]/(top5IDTotalCount[3])*100).toFixed(1) , total: top5IDTotalCount[3] },
    { label: top5IDs[4], failCount: top5IDFailCount[4], passCount: top5IDPassCount[4], score: (top5IDFailCount[4]/(top5IDTotalCount[4])*100).toFixed(1) , total: top5IDTotalCount[4] },
  ];

  const getRiskColor = (riskLevel) => {
    switch(riskLevel) {
      case 'Low': return 'text-green-500';
      case 'Medium': return 'text-yellow-500';
      case 'High': return 'text-orange-500';
      case 'Critical': return 'text-[#FF0000]';
      default: return 'text-white';
    }
  };

  return (
    <div className='flex flex-col h-auto'>
      {loading ? (
        <LoadingPopup isVisible={true}/>
      ) : (
        <LoadingPopup isVisible={false}/> 
      )
      }
      <div className=' h-[15vh] flex flex-row justify-between items-center'>
        <div className='m-12'>
          <p className='text-white font-bold text-4xl'>Dashboard</p>
          <p className='text-[#8D8D8D] text-xl'>Monitor and manage your LLM scans</p>
        </div>

        <div className='flex flex-row w-auto  m-12 gap-6'>
          <button
            onClick={handleNewScan}
            className='rounded-xl bg-blue-600/20 border border-blue-400 h-12 inline-flex items-center px-4 py-2 cursor-pointer'
          >
            <CiPlay1 className='text-blue-400 h-6 m-3'/>
            <p className='text-blue-400 text-l'>New Scan</p>
          </button>
          <button
            onClick={() => navigate('/prompt-hardening')}
            className='rounded-xl bg-purple-600/20 border border-purple-400 h-12 inline-flex items-center px-4 py-2 cursor-pointer'
          >
            <p className='text-purple-400 text-l m-3'>System Prompt Hardening</p>
          </button>
        </div>
      </div>

      <div className='m-7 h-auto'>
        <div className="grid gap-4">
          <div className="grid grid-cols-5 gap-3">

            <div className="border border-[#232325] border-3 border-2 border-2 rounded-xl">
              <div className='flex flex-row justify-between items-center'>
                <p className='text-white text-lg m-4 ml-7'>Security Score</p>
                <img src={Meter} alt='meter' className='h-7 aspect-sqaure m-4'/>
              </div>
              <div>
                <p className='font-bold text-5xl ml-7 text-white'>{score}/100</p>
              </div>
              <div className='rounded-3xl bg-[#232325] h-10 m-4 w-auto'>
                <div 
                  className="bg-blue-500 h-10 rounded-full transition-all" 
                  style={{ width: `${percentage}%` }}
                ></div>
              </div>
            </div>

            <div className="border border-[#232325] border-3 border-2 border-2 rounded-xl">
              <div className='flex flex-row justify-between items-center'>
                <p className='text-white text-lg m-4 ml-7'>Total Scans</p>
                <img src={Alert} alt='meter' className='h-7 aspect-sqaure m-4'/>
              </div>
              <div>
                <p className='font-bold text-5xl ml-7 text-white'>{scanCount}</p>
              </div>
              
            </div>

            <div className="border border-[#232325] border-3 border-2 border-2 rounded-xl">
              <div className='flex flex-row justify-between items-center'>
                <p className='text-white text-lg m-4 ml-7'>Total Issues Found</p>
                <img src={Issues} alt='meter' className='h-7 aspect-sqaure m-4'/>
              </div>
              <div>
                <p className='font-bold text-5xl ml-7 text-white'>{issueCount}</p>
                <p
                  className={`ml-7 ${
                    lastScan === 0 ? 'text-green-500' : 'text-[#FF0000]'
                  }`}
                >
                  +{lastScan} issues from last scan
                </p>
              </div>
            </div>

            <div className="border border-[#232325] border-3 border-2 border-2 rounded-xl">
              <div className='flex flex-row justify-between items-center'>
                <p className='text-white text-lg m-4 ml-7'>Last Scan</p>
                <img src={Calendar} alt='meter' className='h-7 aspect-sqaure m-4'/>
              </div>
              <div>
                <p className='font-bold text-5xl ml-7 text-white'>{lastScanTime}</p>
                <p className='text-white text-l ml-7 m-3'>{date}</p>
              </div>
            </div>

            
            <div className="border border-[#232325] border-3 border-2 border-2 rounded-xl">
              <div className='flex flex-row justify-between items-center'>
                <p className='text-white text-lg m-4 ml-7'>Framework Review</p>
                <img src={Meter} alt='meter' className='h-7 aspect-sqaure m-4'/>
              </div>
              <div>
                {governanceScore && Number(governanceScore.overall.total_score) !== 0 ? (
                  <>
                    <div className='flex flex-row items-end gap-2'>
                      <p className='font-bold text-5xl ml-7 text-white '>
                        {(governanceScore.overall.total_score/governanceScore.overall.max_possible_score * 100).toFixed(1)}%  
                      </p>
                      <p className="text-lg text-white font-bold font-normal">  Compliant</p>
                    </div>
                    
                    <p className={`ml-7 ${getRiskColor(governanceScore.overall.risk_level)}`}>
                      {governanceScore.overall.risk_level} Risk
                    </p>
                  </>
                ) : (
                  <>
                    <p className='font-bold text-5xl ml-7 text-white'>--</p>
                    <p className='text-white ml-7'>No assessment yet</p>
                  </>
                )}
              </div>
            </div>

          </div>


          <div className="grid grid-cols-2 gap-3">
            <div className='border border-[#232325] border-3 border-2 border-2 rounded-xl'>
              <div className="bg-[#101012] p-4 rounded-lg">
                <h2 className="text-white text-3xl mb-3 font-semibold">Security Score Trend</h2>
                <p className="text-gray-400 text-sm">Security score evolution over the last 6 months</p>

                <ResponsiveContainer width="100%" height={350}>
                  <AreaChart data={data} margin={{ top: 60, right: 20, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorScore" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#1E90FF" stopOpacity={0.6} />
                        <stop offset="95%" stopColor="#1E90FF" stopOpacity={0} />
                      </linearGradient>
                    </defs>

                    <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2} />
                    <XAxis dataKey="month" tick={{ fill: "gray" }} />
                    <YAxis domain={[50, 100]} tick={{ fill: "gray" }} />
                    <Tooltip />
                    
                    <Area type="monotone" dataKey="score" stroke="#1E90FF" fill="url(#colorScore)" strokeWidth={2} />
                    
                    <Line type="monotone" dataKey="score" stroke="white" strokeWidth={2} dot={{ fill: "white", r: 4 }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-[#101012] border border-[#232325] border-3 border-2 border-2 p-4 rounded-lg w-auto text-white">
              <h2 className="text-3xl mb-3 font-semibold">Weakest Areas</h2>
              <p className="text-gray-400 text-sm">Most vulnerable detectors triggered</p>

              <div className="mt-4 space-y-3">
                {detectors
                  .filter((detector) => detector.label)
                  .map((detector, index) => (
                    <div key={index} className="flex items-center justify-between border-t-2 border-[#232325] pt-2">
                      <div>
                        <p className="text-white text-lg font-bold">{detector.label}</p>
                        <p className="text-sm ">{detector.failCount} out of {detector.total} Occurrences</p>
                      </div>
                      <div className="flex items-center justify-between gap-6">
                        <div className="flex flex-col gap-1 text-sm font-medium">
                          <span className="text-green-400">
                            {detector.passCount} <span className="ml-1">Passed</span> 
                          </span>
                          <span className="text-red-500">
                            {detector.failCount} <span className="ml-1">Failed</span>
                          </span>
                        </div>
                        <div className="w-24 h-24">
                          <CircularProgressbar
                            value={detector.score}
                            text={`${detector.score}`}
                            styles={buildStyles({
                              textSize: '20px',
                              pathColor: '#FF0000',
                              textColor: 'white',
                              trailColor: '#4CAF50',
                              backgroundColor: '#1E1E1E'
                            })}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>

        </div>
        
      </div>
      <div className='rounded-xl flex flex-col text-white m-6 mt-0  border border-[#232325] border-3 '>
        <Reports/>
        <div className='flex justify-center items-center'>
          <button
            type="button"
            onClick={() => navigate('/reports')}
            className="w-30 text-white border border-[#232325] border-3 border-2 border-2 mb-6 rounded-xl cursor-pointer"
          >
            <p className='m-3'>View More</p>
          </button>
        </div>
        
      </div>
    </div>
    
  )
}

export default Dashboard